import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileSpreadsheet, Info, CheckCircle, XCircle, Download } from 'lucide-react';
import { useImport } from '@/hooks/useImport';
import { useToast } from '@/components/ui/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import ExcelJS from 'exceljs';

type ImportType = 'categories' | 'accounts' | 'suppliers' | 'paymentMethods' | 'costCenters';

interface ImportCadastroButtonProps {
  type: ImportType;
  label: string;
  disabled?: boolean;
}

const importConfigs = {
  categories: {
    title: 'Importar Plano de Contas',
    description: 'Importar categorias do plano de contas',
    columns: [
      { name: "Nome", description: "Nome da categoria (obrigatório)" },
      { name: "Tipo", description: "receita ou despesa (obrigatório)" }
    ],
    exampleData: [
      { Nome: "Vendas de Produtos", Tipo: "receita" },
      { Nome: "Salários", Tipo: "despesa" }
    ]
  },
  accounts: {
    title: 'Importar Contas Financeiras',
    description: 'Importar contas bancárias e financeiras',
    columns: [
      { name: "Nome", description: "Nome da conta (obrigatório)" },
      { name: "Saldo Inicial", description: "Saldo inicial em reais (opcional, padrão: 0)" }
    ],
    exampleData: [
      { Nome: "Conta Corrente Banco do Brasil", "Saldo Inicial": 5000.00 },
      { Nome: "Caixa", "Saldo Inicial": 500.00 }
    ]
  },
  suppliers: {
    title: 'Importar Fornecedores',
    description: 'Importar lista de fornecedores',
    columns: [
      { name: "Nome", description: "Nome do fornecedor (obrigatório)" }
    ],
    exampleData: [
      { Nome: "Fornecedor ABC Ltda" },
      { Nome: "Distribuidora XYZ S.A." }
    ]
  },
  paymentMethods: {
    title: 'Importar Formas de Pagamento',
    description: 'Importar formas de pagamento',
    columns: [
      { name: "Nome", description: "Nome da forma de pagamento (obrigatório)" }
    ],
    exampleData: [
      { Nome: "Dinheiro" },
      { Nome: "Cartão de Crédito" },
      { Nome: "PIX" }
    ]
  },
  costCenters: {
    title: 'Importar Centros de Custo',
    description: 'Importar centros de custo',
    columns: [
      { name: "Nome", description: "Nome do centro de custo (obrigatório)" }
    ],
    exampleData: [
      { Nome: "Administrativo" },
      { Nome: "Vendas" },
      { Nome: "Produção" }
    ]
  }
};

export const ImportCadastroButton: React.FC<ImportCadastroButtonProps> = ({
  type,
  label,
  disabled = false
}) => {
  const { 
    importing, 
    importCategories, 
    importAccounts, 
    importSuppliers, 
    importPaymentMethods, 
    importCostCenters
  } = useImport();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  const config = importConfigs[type];

  const getImportFunction = () => {
    switch (type) {
      case 'categories': return importCategories;
      case 'accounts': return importAccounts;
      case 'suppliers': return importSuppliers;
      case 'paymentMethods': return importPaymentMethods;
      case 'costCenters': return importCostCenters;
      default: throw new Error('Tipo de importação não suportado');
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast({
        title: "Erro",
        description: "Por favor, selecione um arquivo Excel (.xlsx ou .xls)",
        variant: "destructive"
      });
      return;
    }

    try {
      const importFunction = getImportFunction();
      const result = await importFunction(file);
      setImportResult(result);
      
      toast({
        title: result.success ? "Importação Concluída" : "Erro na Importação",
        description: result.message,
        variant: result.success ? "default" : "destructive"
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro inesperado durante a importação",
        variant: "destructive"
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    setImportResult(null);
    fileInputRef.current?.click();
  };

  const generateExampleFile = async () => {
    const config = importConfigs[type];
    
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Dados');

    // Add header row
    const headers = Object.keys(config.exampleData[0]);
    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };

    // Add data rows
    config.exampleData.forEach(row => {
      worksheet.addRow(Object.values(row));
    });

    // Auto-fit column widths
    worksheet.columns.forEach((column) => {
      column.width = 25;
    });

    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `exemplo_${type}.xlsx`;
    link.click();
    
    URL.revokeObjectURL(url);
    
    toast({
      title: "Arquivo de exemplo gerado",
      description: "O arquivo Excel de exemplo foi baixado com sucesso!",
    });
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            disabled={disabled || importing}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            {importing ? 'Importando...' : label}
          </Button>
        </DialogTrigger>
        
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              {config.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {config.description}. O arquivo deve conter uma linha de cabeçalho seguida pelos dados.
              </AlertDescription>
            </Alert>

            <div>
              <h4 className="text-sm font-medium mb-2">Colunas Necessárias:</h4>
              <ScrollArea className="h-32 border rounded-md p-3">
                <div className="space-y-2">
                  {config.columns.map((column, index) => (
                    <div key={index} className="text-sm">
                      <span className="font-medium text-primary">{column.name}</span>
                      <span className="text-muted-foreground ml-2">- {column.description}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {importResult && (
              <Alert className={importResult.success ? "border-green-500" : "border-red-500"}>
                {importResult.success ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
                <AlertDescription>
                  {importResult.message}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium">Erros encontrados:</p>
                      <ScrollArea className="h-24 mt-1">
                        <ul className="text-xs space-y-1">
                          {importResult.errors.map((error: string, index: number) => (
                            <li key={index} className="text-red-600">• {error}</li>
                          ))}
                        </ul>
                      </ScrollArea>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button 
                onClick={handleButtonClick} 
                disabled={importing}
                className="flex-1"
              >
                <Upload className="h-4 w-4 mr-2" />
                {importing ? 'Importando...' : 'Selecionar Arquivo Excel'}
              </Button>
              
              <Button 
                variant="secondary" 
                onClick={generateExampleFile}
                disabled={importing}
              >
                <Download className="h-4 w-4 mr-2" />
                Exemplo
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => setIsOpen(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
